import { useEffect, useState } from "react";
import { getPayments, declarePayment, getRoom } from "../../firebase/services";
import { Badge, EmptyState, Spinner, Btn } from "../../components/UI";
import { genererQuittance } from "../../utils/quittance";

const LOYER = 27500;
const METHODS = ["Orange Money", "Wave", "Espèces", "Virement"];
const METHOD_ICONS = { "Orange Money":"🟠", "Wave":"🌊", "Espèces":"💵", "Virement":"🏦" };
const MOIS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

function formatMonthValue(val) {
  if (!val) return "";
  try {
    const [year, month] = val.split("-");
    return `${MOIS[parseInt(month) - 1]} ${year}`;
  } catch(e) { return val; }
}

function formatDate(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("fr-FR", { day:"2-digit", month:"short", year:"numeric" });
}

function getMonthOptions() {
  const now = new Date();
  const options = [];
  // 11 mois passés + mois actuel + 12 mois à venir
  for (let i = -11; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = d.toISOString().slice(0, 7);
    const label = i === 0
      ? `${MOIS[d.getMonth()]} ${d.getFullYear()} (mois en cours)`
      : i > 0
        ? `${MOIS[d.getMonth()]} ${d.getFullYear()} — avance`
        : `${MOIS[d.getMonth()]} ${d.getFullYear()}`;
    options.push({ value, label, isFuture: i > 0, isCurrent: i === 0 });
  }
  return options;
}

export default function StudentPayments({ roomId, onBack }) {
  const [payments, setPayments]     = useState([]);
  const [room, setRoom]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [view, setView]             = useState("history");
  const [method, setMethod]         = useState("Orange Money");
  const [reference, setReference]   = useState("");
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded]     = useState(null);

  const monthOptions = getMonthOptions();

  useEffect(() => {
    if (!roomId) { setLoading(false); return; }
    const unsub = getPayments(data => { setPayments(data); setLoading(false); }, roomId);
    getRoom(roomId).then(setRoom).catch(() => {});
    return unsub;
  }, [roomId]);

  const hasPending    = payments.some(p => p.status === "pending");
  const monthPaid     = payments.some(p => p.month === selectedMonth && p.status === "confirmed");
  const isFutureMonth = monthOptions.find(o => o.value === selectedMonth)?.isFuture;

  const handleDeclare = async () => {
    if (method !== "Espèces" && !reference.trim()) return;
    setSubmitting(true);
    await declarePayment(roomId, {
      amount: LOYER,
      method,
      reference: reference.trim(),
      month: selectedMonth,
      monthLabel: formatMonthValue(selectedMonth),
    });
    setSubmitting(false);
    setView("done");
  };

  const badgeType  = { confirmed:"ok", pending:"pend", rejected:"late" };
  const badgeLabel = { confirmed:"Payé ✓", pending:"En attente", rejected:"Rejeté ✗" };

  if (loading) return <div style={S.page}><Spinner /></div>;

  // GARDE : aucun accès aux paiements sans chambre associée
  if (!roomId) return (
    <div style={S.page}>
      <div style={S.header}>
        <button style={S.back} onClick={onBack}>←</button>
        <span style={S.htitle}>Paiements</span>
      </div>
      <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:28, textAlign:"center" }}>
        <div style={{ fontSize:40, marginBottom:12 }}>🏠</div>
        <p style={{ fontSize:16, fontWeight:700, color:"#8a4c00", margin:"0 0 8px" }}>Aucune chambre associée</p>
        <p style={{ fontSize:14, color:"#7a90a8", lineHeight:1.6 }}>
          Votre compte n'est rattaché à aucune chambre. Contactez IAM Business Group pour être associé à votre logement avant de déclarer un paiement.
        </p>
      </div>
    </div>
  );
  if (view === "declare") return (
    <div style={S.page}>
      <div style={S.header}>
        <button style={S.back} onClick={() => setView("history")}>←</button>
        <span style={S.htitle}>Déclarer un paiement</span>
      </div>
      <div style={S.scroll}>
        <div style={S.form}>
          <div style={S.amountCard}>
            <p style={S.amountLabel}>Montant</p>
            <p style={S.amountNum}>{LOYER.toLocaleString("fr-FR")} FCFA</p>
            <p style={S.amountSub}>Loyer mensuel — IAM Business Group</p>
          </div>

          <p style={S.fieldLabel}>Mois à payer</p>
          <select style={S.select} value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
            <optgroup label="── Mois passés ──">
              {monthOptions.filter(o => !o.isFuture && !o.isCurrent).map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </optgroup>
            <optgroup label="── Mois en cours ──">
              {monthOptions.filter(o => o.isCurrent).map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </optgroup>
            <optgroup label="── Paiement en avance ──">
              {monthOptions.filter(o => o.isFuture).map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </optgroup>
          </select>

          {monthPaid && (
            <div style={S.warnBox}>⚠️ {formatMonthValue(selectedMonth)} est déjà marqué comme payé.</div>
          )}
          {isFutureMonth && !monthPaid && (
            <div style={S.infoBox}>
              💡 Vous payez en avance pour <strong>{formatMonthValue(selectedMonth)}</strong>.
            </div>
          )}

          <p style={S.fieldLabel}>Mode de paiement</p>
          <div style={S.methodGrid}>
            {METHODS.map(m => (
              <button key={m}
                style={{ ...S.methodBtn, ...(method === m ? S.methodOn : {}) }}
                onClick={() => setMethod(m)}
              >
                <span style={{ fontSize:20 }}>{METHOD_ICONS[m]}</span>
                <span style={{ fontSize:11, fontWeight:600 }}>{m}</span>
              </button>
            ))}
          </div>

          {method !== "Espèces" && (
            <>
              <p style={S.fieldLabel}>Référence de transaction</p>
              <input style={S.input} value={reference} onChange={e => setReference(e.target.value)}
                placeholder={method === "Orange Money" ? "Ex: OM-20260609-XXXXX" : method === "Wave" ? "Ex: WV-XXXXXXXXXX" : "Référence virement"}
              />
            </>
          )}

          <div style={{ marginTop:20 }}>
            <Btn variant="primary" onClick={handleDeclare}
              disabled={submitting || (method !== "Espèces" && !reference.trim())}>
              {submitting ? "Envoi..." : `✓ Déclarer — ${formatMonthValue(selectedMonth)}`}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );

  // ── SUCCÈS ───────────────────────────────────────────────
  if (view === "done") return (
    <div style={S.page}>
      <div style={S.header}><span style={S.htitle}>Paiement déclaré</span></div>
      <div style={S.successWrap}>
        <div style={S.successIcon}>✓</div>
        <h2 style={S.successTitle}>Paiement déclaré !</h2>
        <p style={S.successSub}>
          Paiement de <strong>{LOYER.toLocaleString("fr-FR")} FCFA</strong> pour{" "}
          <strong>{formatMonthValue(selectedMonth)}</strong> transmis à IAM.
          Vous recevrez une notification dès confirmation.
        </p>
        <div style={{ padding:"0 24px", marginTop:24, width:"100%" }}>
          <Btn variant="primary" onClick={() => setView("history")}>Voir l'historique</Btn>
        </div>
      </div>
    </div>
  );

  // ── HISTORIQUE ───────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={S.header}>
        <button style={S.back} onClick={onBack}>←</button>
        <span style={S.htitle}>Paiements</span>
      </div>
      <div style={S.scroll}>
        <div style={S.summaryCard}>
          <div>
            <p style={S.summaryLabel}>Loyer mensuel</p>
            <p style={S.summaryAmount}>{LOYER.toLocaleString("fr-FR")} FCFA</p>
          </div>
          {!hasPending && (
            <button style={S.declareBtn} onClick={() => setView("declare")}>💳 Déclarer</button>
          )}
        </div>

        {hasPending && (
          <div style={S.pendingBanner}>
            <span>⏳</span>
            <p style={{ fontSize:13, color:"#8a6a00", margin:0 }}>
              Paiement en cours de vérification par IAM
            </p>
          </div>
        )}

        {/* Aide WhatsApp */}
        <a
          href="https://wa.me/22377727771?text=Bonjour%20IAM%2C%20j%27ai%20une%20question%20concernant%20mon%20loyer."
          target="_blank" rel="noreferrer"
          style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, background:"#e9f9ef", border:"1px solid #a8e6c1", borderRadius:12, padding:"10px", marginBottom:10, textDecoration:"none", fontSize:12.5, fontWeight:600, color:"#1a7a3c" }}
        >
          📱 Un problème avec un paiement ? Écrivez-nous sur WhatsApp
        </a>

        {/* Stats */}
        {payments.length > 0 && (
          <div style={S.statsRow}>
            <div style={S.statBox}>
              <span style={S.statNum}>{payments.filter(p => p.status==="confirmed").length}</span>
              <span style={S.statLabel}>Confirmés</span>
            </div>
            <div style={S.statBox}>
              <span style={{...S.statNum, color:"#8a4c00"}}>{payments.filter(p => p.status==="pending").length}</span>
              <span style={S.statLabel}>En attente</span>
            </div>
            <div style={S.statBox}>
              <span style={{...S.statNum, color:"#1a3a5c", fontSize:14}}>
                {(payments.filter(p => p.status==="confirmed").length * LOYER).toLocaleString("fr-FR")}
              </span>
              <span style={S.statLabel}>FCFA payés</span>
            </div>
          </div>
        )}

        <p style={S.sectionTitle}>Historique des transactions</p>
        {payments.length === 0 ? (
          <EmptyState icon="📋" title="Aucun paiement" subtitle="Vos paiements déclarés apparaîtront ici" />
        ) : payments.map(p => (
          <div key={p.id} style={S.payCard} onClick={() => setExpanded(expanded === p.id ? null : p.id)}>
            <div style={S.payMain}>
              <span style={{ fontSize:22 }}>{METHOD_ICONS[p.method] || "💳"}</span>
              <div style={{ flex:1 }}>
                <p style={S.payMonth}>
                  {p.monthLabel || (p.month ? formatMonthValue(p.month) : null) || "Paiement"}
                </p>
                <p style={S.payMeta}>{p.method || "—"}</p>
              </div>
              <div style={{ textAlign:"right" }}>
                <p style={S.payAmt}>{LOYER.toLocaleString("fr-FR")} FCFA</p>
                <Badge type={badgeType[p.status]||"late"}>{badgeLabel[p.status]||"?"}</Badge>
              </div>
            </div>

            {expanded === p.id && (
              <div style={S.details}>
                <div style={S.dRow}><span style={S.dl}>Mois</span><span style={S.dv}>{p.monthLabel || (p.month ? formatMonthValue(p.month) : "—")}</span></div>
                <div style={S.dRow}><span style={S.dl}>Déclaré le</span><span style={S.dv}>{formatDate(p.createdAt)}</span></div>
                {p.confirmedAt && <div style={S.dRow}><span style={S.dl}>Confirmé le</span><span style={S.dv}>{formatDate(p.confirmedAt)}</span></div>}
                {p.rejectedAt  && <div style={S.dRow}><span style={S.dl}>Rejeté le</span><span style={S.dv}>{formatDate(p.rejectedAt)}</span></div>}
                {p.rejectReason && <div style={S.dRow}><span style={S.dl}>Raison rejet</span><span style={{ ...S.dv, color:"#a32d2d" }}>{p.rejectReason}</span></div>}
                <div style={S.dRow}><span style={S.dl}>Mode</span><span style={S.dv}>{METHOD_ICONS[p.method]} {p.method}</span></div>
                {p.reference && <div style={S.dRow}><span style={S.dl}>Référence</span><span style={{ ...S.dv, fontFamily:"monospace", fontSize:12 }}>{p.reference}</span></div>}
                <div style={S.dRow}><span style={S.dl}>Montant</span><span style={{ ...S.dv, fontWeight:700, color:"#1a3a5c" }}>{LOYER.toLocaleString("fr-FR")} FCFA</span></div>
                {p.status === "confirmed" && (
                  <button
                    style={{ width:"100%", marginTop:10, padding:"10px", borderRadius:10, background:"#1a3a5c", color:"white", border:"none", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}
                    onClick={e => { e.stopPropagation(); genererQuittance({ payment: p, room }); }}
                  >
                    📄 Télécharger la quittance de loyer
                  </button>
                )}
              </div>
            )}
            <p style={{ fontSize:10, color:"#aab8c8", textAlign:"right", margin:"4px 0 0" }}>
              {expanded === p.id ? "▲ Masquer" : "▼ Détails"}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

const S = {
  page:   { display:"flex", flexDirection:"column", height:"100%", background:"#f5f8fc", fontFamily:"'Barlow',sans-serif" },
  header: { background:"#1a3a5c", padding:"10px 14px", display:"flex", alignItems:"center", gap:10 },
  back:   { background:"none", border:"none", color:"white", fontSize:20, cursor:"pointer", padding:"0 8px 0 0" },
  htitle: { flex:1, fontSize:15, fontWeight:700, color:"white" },
  scroll: { flex:1, overflowY:"auto", padding:"12px 16px 80px" },
  summaryCard: { background:"linear-gradient(135deg,#1a3a5c,#2e6aa0)", borderRadius:16, padding:"16px 20px", marginBottom:10, display:"flex", alignItems:"center", justifyContent:"space-between" },
  summaryLabel:  { fontSize:12, color:"#90bde0", margin:"0 0 2px" },
  summaryAmount: { fontSize:24, fontWeight:800, color:"white", margin:0, fontFamily:"'Barlow Condensed',sans-serif" },
  declareBtn: { background:"rgba(255,255,255,0.15)", border:"none", color:"white", borderRadius:10, padding:"10px 16px", fontSize:13, fontWeight:600, cursor:"pointer" },
  pendingBanner: { background:"#fff9e6", borderRadius:12, padding:"12px 14px", marginBottom:10, display:"flex", alignItems:"center", gap:10, border:"1.5px solid #f5d8a0" },
  statsRow: { display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:12 },
  statBox:  { background:"white", borderRadius:10, padding:"10px 8px", textAlign:"center", border:"0.5px solid #d0dce8" },
  statNum:  { display:"block", fontSize:18, fontWeight:800, color:"#1a3a5c", fontFamily:"'Barlow Condensed',sans-serif" },
  statLabel:{ display:"block", fontSize:10, color:"#7a90a8", marginTop:2 },
  sectionTitle: { fontSize:11, textTransform:"uppercase", letterSpacing:"0.1em", color:"#7a90a8", fontWeight:600, margin:"8px 0 6px" },
  payCard:  { background:"white", borderRadius:12, padding:"12px 14px", marginBottom:8, border:"0.5px solid #d0dce8", cursor:"pointer" },
  payMain:  { display:"flex", alignItems:"center", gap:12 },
  payMonth: { fontSize:13, fontWeight:700, color:"#1a2a3a", margin:0, textTransform:"capitalize" },
  payMeta:  { fontSize:11, color:"#7a90a8", margin:0 },
  payAmt:   { fontSize:13, fontWeight:700, color:"#1a3a5c", margin:"0 0 3px", fontFamily:"'Barlow Condensed',sans-serif" },
  details:  { marginTop:10, paddingTop:10, borderTop:"0.5px solid #e8f0f8" },
  dRow:     { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"5px 0", borderBottom:"0.5px solid #f0f4f8" },
  dl:       { fontSize:12, color:"#7a90a8" },
  dv:       { fontSize:12, color:"#1a2a3a" },
  form:     { padding:"16px" },
  amountCard:  { background:"linear-gradient(135deg,#1a3a5c,#2e6aa0)", borderRadius:16, padding:"18px 20px", marginBottom:20 },
  amountLabel: { fontSize:12, color:"#90bde0", margin:"0 0 2px" },
  amountNum:   { fontSize:28, fontWeight:800, color:"white", margin:"0 0 2px", fontFamily:"'Barlow Condensed',sans-serif" },
  amountSub:   { fontSize:12, color:"#90bde0", margin:0 },
  fieldLabel:  { fontSize:13, fontWeight:600, color:"#2a4a6a", marginBottom:8 },
  select:  { width:"100%", border:"1.5px solid #d0dce8", borderRadius:10, padding:"12px", fontSize:14, fontFamily:"inherit", background:"#f7fafd", color:"#1a2a3a", outline:"none", boxSizing:"border-box", marginBottom:16, cursor:"pointer" },
  warnBox: { background:"#fff3e0", border:"1px solid #f5d8a0", borderRadius:10, padding:"10px 12px", fontSize:12, color:"#8a4c00", marginBottom:12 },
  infoBox: { background:"#e3f1fb", border:"1px solid #90bde0", borderRadius:10, padding:"10px 12px", fontSize:12, color:"#1a3a5c", marginBottom:12 },
  methodGrid: { display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8, marginBottom:16 },
  methodBtn:  { background:"white", border:"1.5px solid #d0dce8", borderRadius:12, padding:"12px", display:"flex", flexDirection:"column", alignItems:"center", gap:6, cursor:"pointer", fontFamily:"inherit", color:"#5a7a9a" },
  methodOn:   { background:"#e3f1fb", borderColor:"#2e86c1", color:"#1a3a5c" },
  input:  { width:"100%", border:"1.5px solid #d0dce8", borderRadius:10, padding:"12px", fontSize:14, fontFamily:"inherit", background:"#f7fafd", color:"#1a2a3a", outline:"none", boxSizing:"border-box", marginBottom:4 },
  successWrap: { flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24 },
  successIcon: { width:64, height:64, borderRadius:"50%", background:"#e8f5e0", color:"#2d6a0a", fontSize:30, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:16 },
  successTitle: { fontSize:22, fontWeight:800, color:"#1a3a5c", marginBottom:8, fontFamily:"'Barlow Condensed',sans-serif" },
  successSub:   { fontSize:14, color:"#5a7a9a", textAlign:"center", lineHeight:1.6 },
};
